import { formatMoney, backupKlaviyoBackInStock, restoreKlaviyoBackInStock } from '@theme/utilities';
import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.addToCartButton = null;
    this.addToCartButtonContainer = null;
    this.addToCartBehavior = null;
    this.toggleCrossSells = null;
    this.quantityBreaks = null;
    this.quantitySelectorInput = null;
  }

  connectedCallback() {
    this.form = this.querySelector('form[action$="/cart/add"]');
    this.addToCartButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.addToCartButtonContainer = this.querySelector('[data-ref="add-to-cart-button-container"]');
    this.addToCartBehavior = document.querySelector('cart-icon')?.addToCartBehavior || 'open_cart';
    this.toggleCrossSells = this.querySelector('[data-ref="toggle-cross-sells"]');
    this.quantityBreaks = this.querySelector('[data-ref="quantity-breaks"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="product-quantity-selector"] input[name="quantity"]');

    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.addEventListener(ThemeEvents.variantSelected, this.onVariantSelected.bind(this));
    this.addEventListener(ThemeEvents.variantUpdated, this.onFormUpdated.bind(this));
    document.addEventListener(ThemeEvents.toggleCrossSellsUpdated, this.onFormUpdated.bind(this));
    document.addEventListener(ThemeEvents.quantityBreaksUpdated, this.onFormUpdated.bind(this));
    this.quantitySelectorInput?.addEventListener('change', this.onFormUpdated.bind(this));
  }

  disconnectedCallback() {
    this.form.removeEventListener('submit', this.handleSubmit.bind(this));
    this.removeEventListener(ThemeEvents.variantSelected, this.onVariantSelected.bind(this));
    this.removeEventListener(ThemeEvents.variantUpdated, this.onFormUpdated.bind(this));
    document.removeEventListener(ThemeEvents.toggleCrossSellsUpdated, this.onFormUpdated.bind(this));
    document.removeEventListener(ThemeEvents.quantityBreaksUpdated, this.onFormUpdated.bind(this));
    this.quantitySelectorInput?.removeEventListener('change', this.onFormUpdated.bind(this));
  }

  onVariantSelected() {
    this.addToCartButton.disabled = true;
  }

  onFormUpdated(event) {
    // On récupère le DOM actuel du composant
    const addToCartButtonContainer = this.addToCartButtonContainer;

    // Le event.detail est null si l'évenement de mise à jour est déclenché par les cross-sells à activer et non par les variantes, dans ce cas là on utilise le DOM actuel
    let newAddToCartButtonContainer = addToCartButtonContainer;
    if (event && event.detail != null) {
      newAddToCartButtonContainer = event.detail.data.html.querySelector('[data-ref="add-to-cart-button-container"]');
    }

    if (!newAddToCartButtonContainer || !addToCartButtonContainer) return;

    const klaviyoBisContainer = backupKlaviyoBackInStock(addToCartButtonContainer);

    // On remplace le DOM actuel par le nouveau DOM
    if (addToCartButtonContainer !== newAddToCartButtonContainer) {
      addToCartButtonContainer.replaceWith(newAddToCartButtonContainer);
    }

    // On réassigne les nouvelles valeurs du bouton pour le reste du fonctionnement du composant
    this.addToCartButton = newAddToCartButtonContainer.querySelector('[data-ref="add-to-cart-button"]');
    this.addToCartButtonContainer = newAddToCartButtonContainer;

    restoreKlaviyoBackInStock(this.addToCartButtonContainer, klaviyoBisContainer);

    // On récupère les prix totaux du formulaire (incluant le produit de base, sa quantité, les cross-sells à activer, etc.)
    const newTotalPrice = this.getCurrentTotalPrice().price;
    const newTotalCompareAtPrice = this.getCurrentTotalPrice().compare_at_price;

    // On met à jour les prix dans le nouveau DOM à insérer
    const priceElement = this.addToCartButtonContainer.querySelector('[data-ref="price"]');
    if (priceElement != null) {
      priceElement.innerHTML = formatMoney(newTotalPrice);
    }

    const compareAtPriceElement = this.addToCartButtonContainer.querySelector('[data-ref="compare-at-price"]');
    if (compareAtPriceElement != null) {
      compareAtPriceElement.innerHTML = '';
      if (newTotalCompareAtPrice > newTotalPrice) {
        compareAtPriceElement.innerHTML = formatMoney(newTotalCompareAtPrice);
      }
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    this.addToCartButton.classList.add('is-loading');

    let items = [];
    if (this.quantityBreaks) {
      items = [...items, ...this.quantityBreaks.getSelectedItems()];
    } else {
      let properties = {};
      this.form.querySelectorAll('input[name^="properties["]').forEach((input) => {
        properties[input.name.replace('properties[', '').replace(']', '')] = input.value;
      });

      const sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
      const sellingPlan = sellingPlanInput?.value;

      const itemData = {
        id: this.form.querySelector('input[name="id"]').value,
        quantity: parseInt(this.form.querySelector('input[name="quantity"]')?.value, 10) || 1,
      };

      if (properties.length > 0) {
        itemData.properties = properties;
      }

      if (sellingPlan) {
        itemData.selling_plan = sellingPlan;
      }

      items.push(itemData);
    }

    if (this.toggleCrossSells) {
      items = [...items, ...this.toggleCrossSells.getSelectedItems().items];
    }

    let quantity = items.reduce((acc, item) => acc + item.quantity, 0);

    const sections = [];
    if (document.querySelector('cart-drawer')) {
      const section_cart_drawer_id = document.querySelector('cart-drawer').dataset.sectionId;
      sections.push(section_cart_drawer_id);
    }

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: items,
        sections: sections.join(','),
      }),
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
        // Attention à bien revoir ca
        const itemCountToAdd = quantity || 1;

        let newItemCount;
        if (currentItemCount != null) {
          newItemCount = currentItemCount + itemCountToAdd;
        }

        // On dispatch l'événement de mise à jour du panier
        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: newItemCount,
              source: 'product-form',
              sections: response.sections,
            },
          ),
        );

        this.addToCartButton.classList.remove('is-loading');
      })
      .then(() => {
        if (this.addToCartBehavior == 'notification') {
          document.dispatchEvent(
            new CustomEvent('toast:open', {
              detail: { type: 'success', message: Theme.translations.addToCartNotificationMessage },
            }),
          );
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: { type: 'error', message: error },
          }),
        );
      });
  }

  // Permet de récupérer les prix totaux du formulaire (incluant le produit de base, sa quantité, les cross-sells à activer, etc.)
  getCurrentTotalPrice() {
    let newPrice = parseInt(this.addToCartButtonContainer.dataset.price);
    let newCompareAtPrice = newPrice;
    if (this.addToCartButtonContainer.dataset.compareAtPrice) {
      newCompareAtPrice = parseInt(this.addToCartButtonContainer.dataset.compareAtPrice);
    }

    if (this.quantityBreaks) {
      newPrice = parseInt(this.quantityBreaks.getPrices().price);
      newCompareAtPrice = parseInt(this.quantityBreaks.getPrices().compareAtPrice);
    } else if (this.quantitySelectorInput) {
      const quantity = this.quantitySelectorInput.value;
      newPrice = newPrice * quantity;
      newCompareAtPrice = newCompareAtPrice * quantity;
    }

    if (this.toggleCrossSells) {
      newPrice += parseInt(this.toggleCrossSells.getSelectedItems().total_price);
      newCompareAtPrice += parseInt(this.toggleCrossSells.getSelectedItems().total_compare_at_price);
    }

    const differencePrice = newCompareAtPrice - newPrice;

    return {
      price: newPrice,
      compare_at_price: newCompareAtPrice,
      difference_price: differencePrice,
    };
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
